import { CLIENT_API_PROXY_BASE, INTERNAL_API_URL } from '@/ots-constants';
import {
  HistoricalSnowfallType, OpenFlag, ResortStatus, ResortWeatherType, ReviewsOrderBy,
} from '@/api/resort/types';
import { ResortTopRated } from '@/api/home/types';
import { WeskiEntityType } from '@/api/weski/types';
import { IGridSorting, SortingType } from '@/components/common/Grid/types';
import { buildQuery } from '@/api/buildQuery';

const isServer = typeof window === 'undefined';
// Server-side we call the API directly. In the browser we go through the
// same-origin proxy, so no request from a user's machine reaches the API
// host — see app/api/ots/[...path]/route.ts.
const apiUrl = isServer ? INTERNAL_API_URL : CLIENT_API_PROXY_BASE;

export const staticRoutes = {
  translations: () => `${apiUrl}/api/v2/translations`,
  urlTranslations: () => `${apiUrl}/api/v2/urls`,

  resortList: () => `${apiUrl}/api/v2/resorts-all`,
  regionList: () => `${apiUrl}/api/v2/regions`,
  newsCategoriesList: () => `${apiUrl}/api/v2/news/categories`,
  weskiMappings: (params?: { entityType?: WeskiEntityType, entityId?: string }) => (
    `${apiUrl}/api/v2/weskiMappings${buildQuery({
      entityType: params?.entityType,
      entityId: params?.entityId,
    })}`
  ),

  /**
   * RESORTS
   */
  resortLastFirstHandReport: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/last-report`,
  resortReports: (resortUUID: string, page: number, limit: number) => `${apiUrl}/api/v3/resort/${resortUUID}/reports?page=${page}&limit=${limit}`,
  resortReviewsPages: (
    resortUUID: string,
    page: number,
    orderBy: ReviewsOrderBy,
  ) => `${apiUrl}/api/v2/resort/${resortUUID}/reviews/pages/${page}?orderBy=${orderBy}`,
  resortReviews: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/reviews`,
  resortDescriptionTabs: (resortUUID: string) => (
    `${apiUrl}/api/v2/resort/${resortUUID}/descriptions`
  ),
  resortStrengths: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/strengths`,
  resortSnowReport: (resortUUID: string) => `${apiUrl}/api/v3/resort/${resortUUID}/snowreport`, // NOTE: Version 3
  resortRelated: (resortUUID: string) => `${apiUrl}/api/v3/resort/${resortUUID}/related`,
  resortSkiRentalsAlpin: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/skirentals/alpin`,
  resortShortInfo: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}`,
  resortShortWeatherInfo: (resortUUID: string) => `${apiUrl}/api/v3/resort/${resortUUID}/shortreport`,
  resortFeaturedCam: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/featuredcam`,
  resortCameras: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/webcams`,
  resortWeatherItems: (resortUUID: string, type: ResortWeatherType.daily | ResortWeatherType.hourly) => (
    `${apiUrl}/api/v3/resort/${resortUUID}/weather/${type}`
  ),
  resortSnowfallItems: (resortUUID: string, type: HistoricalSnowfallType.monthly | HistoricalSnowfallType.annual) => (
    `${apiUrl}/api/v2/resort/${resortUUID}/snowfall/${type}`
  ),
  resortTrailMap: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/trailMap`,
  resortSubscribe: () => `${apiUrl}/api/v2/subscribe`,
  resortRelatedNews: (resortUUID: string, summer: boolean = false) => `${apiUrl}/api/v2/resort/${resortUUID}/articles${summer ? '?summer=true' : ''}`,
  resortDailyLiftTickets: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/lift-tickets`,
  resortSeasonLiftTickets: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/season-passes`,
  /**
   * REGIONS
   */
  regionResortsByStatus: (
    regionUUID: string,
    status: OpenFlag | ResortStatus,
    page: number,
    limit: number,
    orderBy?: string,
    direction?: SortingType,
  ) => {
    const path = status === 'projected-closing'
      ? `resorts/closing-dates/page/${page}`
      : `resorts/${status}/page/${page}`;
    const query = buildQuery({ limit, orderBy, direction });
    return `${apiUrl}/api/v2/region/${regionUUID}/${path}${query}`;
  },
  regionRelated: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/related`,
  regionAllResortsTopSnow: (sorting?: IGridSorting) => (
    `${apiUrl}/api/v2/snow/resorts${buildQuery({ orderBy: sorting?.orderBy, direction: sorting?.direction })}`
  ),
  regionRelatedNews: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/articles`,
  regionClosingOpenDates: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/opening-closing-dates`,
  regionMeta: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/meta`,
  resortMeta: (resortUUID: string) => `${apiUrl}/api/v2/resort/${resortUUID}/meta`,
  regionFeaturedCam: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/featuredcam`,
  regionCameras: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/webcams`,
  regionCamerasByStatus: (
    regionUUID: string,
    status: OpenFlag | ResortStatus,
    page: number,
    limit: number,
  ) => `${apiUrl}/api/v2/region/${regionUUID}/webcams/${status}/page/${page}?limit=${limit}`,
  regionFeatured: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/featured`,
  regionResortListGuide: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/guide`,
  regionFAQ: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/faq`,
  regionStats: (regionUUID: string) => `${apiUrl}/api/v2/region/${regionUUID}/stats`,
  regionLiftTicketsPricesByResort: (
    regionUUID: string,
    page: number = 1,
    limit: number = 12,
    orderBy?: string,
    direction?: SortingType,
  ) => (
    `${apiUrl}/api/v2/region/${regionUUID}/lift-tickets/page/${page}${buildQuery({ limit, orderBy, direction })}`
  ),

  /**
   * HOME
   */
  search: (query: string) => `${apiUrl}/api/v2/search?q=${query}`,
  searchPopular: () => `${apiUrl}/api/v2/search/popular`,
  searchNews: (query: string) => `${apiUrl}/api/v2/search/news?q=${query}`,
  backgroundImages: () => `${apiUrl}/api/v2/backgroundImages`,
  snowWebcams: (limit: number) => `${apiUrl}/api/v2/snow/webcams?limit=${limit}`,
  resortTopRating: (type: ResortTopRated) => `${apiUrl}/api/v2/resort/topRated/${type}`,
  regionResortTopRating: (regionUUID: string, type: ResortTopRated) => (
    `${apiUrl}/api/v2/region/${regionUUID}/resort/topRated/${type}`
  ),
  mostReportedSnowfall: (regionUUID: string) => (
    `${apiUrl}/api/v2/region/${regionUUID}/resorts/1/page/0?limit=24&orderBy=last-snow&direction=DESC`
  ),
  lastNews: () => `${apiUrl}/api/v2/news/latestNews`,
  lastNewsWeather: () => `${apiUrl}/api/v2/news/latestNews/weather`,
  newsFeatured: () => `${apiUrl}/api/v2/news/featured`,
  newsFromCategory: (category: string, page: number) => `${apiUrl}/api/v2/news/categories/${category}/${page}`,
  resortFeatured: () => `${apiUrl}/api/v2/resort/featured`,
  firstHandReports: (page: number) => `${apiUrl}/api/v2/region/reports?page=${page}`,
  resortTopRatingPages: (
    type: ResortTopRated,
    page: number,
    limit?: number,
  ) => `${apiUrl}/api/v2/region/resort/topRated/${type}?page=${page}${limit ? `&limit=${limit}` : ''}`,
  regionsListGuide: () => `${apiUrl}/api/v2/guide/regions`,
  nearbyResorts: (
    lat: number,
    lon: number,
    orderBy?: string,
    direction?: SortingType,
  ) => (
    `${apiUrl}/api/v2/resort/nearby/coords${buildQuery({
      lat, lon, orderBy, direction,
    })}`
  ),
  getMultipleResorts: (resortIds: Array<string>) => `${apiUrl}/api/v3/resort/snowreports?resortIds=${resortIds.join(',')}`,
  guideHomeFeatured: () => `${apiUrl}/api/v2/featured/guideHome`,
  snowWeatherHomeFeatured: () => `${apiUrl}/api/v2/featured/snowWeatherHome`,
  contactInformation: (regionUUID: string) => `${apiUrl}/api/v2/resort/${regionUUID}/contactInformation`,
  unsubscribe: () => `${apiUrl}/api/v2/unsubscribe`,
  sendWidgetAgreement: () => `${apiUrl}/api/v2/widget/terms-agreement`,
  getWidgetResorts: (resortsUUIDs: string) => `${apiUrl}/api/v2/widget/resorts?ids=${resortsUUIDs}`,
  getWidgetRegion: (regionUUID: string, flag?: string) => `${apiUrl}/api/v2/widget/${regionUUID}/resorts?status=${flag || 'all'}`,
  getResortPhotos: (resortUUID: string, summer: boolean = false) => `${apiUrl}/api/v2/resort/${resortUUID}/photos${summer ? '?summer=true' : ''}`,
};

export const dynamicRoutes = {};
